<script lang="ts">
  import type { schema } from "@editor/schema";
  import Block from "./Block.svelte";
  import GoogleMap from "./utils/GoogleMap.svelte";
  import { getNewsletterStores } from "../newsletterStores";
  import { DateTimeTZUtils } from "./event/DateTimeTZUtils";
  import Rich from "./utils/Rich.svelte";
  import { isEditor, LinkContent, withDefaults } from "./utils";

  const { design, id } = getNewsletterStores();

  export let block: schema.EventBlock;

  $: [view_block, isDefault] = withDefaults(block);

  $: color = isDefault(view_block.name) ? "#505060" : $design!.color.event.text;
  $: background = isDefault(view_block.name) ? "#eee" : $design!.color.event.background;
</script>

<Block {block} on:click on:delete on:duplicate let:isEmpty showEmpty size={"full"} layouts={["-"]}>
  <div class="transition-all">
    <div class="ada-bg-white overflow-hidden transition-all" style="background-color: {background}">
      <div class="flex w-full justify-between" style="color:{color}">
        <div class="flex-grow-2 h-full p-5">
          <div class:hidden={!view_block.name}>
            <h5 class="ada-text-black text-14" style="color:{color}">Event Information</h5>
            <div class="ada-text-black text-16 font-bold" style="color:{color}">{view_block.name}</div>
          </div>
          <p class="my-1 text-15" style="color:{color}" class:hidden={!view_block.details}>
            {#if view_block.details}
              <Rich content={view_block.details} --link-color={color} context={{ type: "event" }} />
            {/if}
          </p>

          <div class="mb-2">
            <div class="ada-text-black text-14">When?</div>
            <div class="ada-text-black text-16 font-bold" style="color:{color}">
              {DateTimeTZUtils.format(view_block.date)}
            </div>
          </div>

          <div class="mb-2" class:hidden={!view_block.location || view_block.onlineEvent}>
            <div class="text-14">Where?</div>
            <div class="ada-text-black text-16 font-bold" style="color:{color}">{view_block.location}</div>
          </div>

          <!-- <div class="mt-5">
            <button class="flex items-center p-2 px-3 font-medium text-gray-700 bg-white rounded shadow clickable text-15">
              <span class="mr-2 material-icons">event</span>Add to calendar
            </button>
          </div> -->
        </div>

        {#if view_block.location && !view_block.hideMapFromEvent && !view_block.onlineEvent}
          <div class="relative w-1/3 flex-shrink-0">
            <div class="absolute bottom-0 right-0 z-20 m-5">
              <LinkContent
                type="event"
                linkText="Map: ${block.name}"
                cta={{
                  url: "https://maps.google.com/maps?daddr=" + encodeURIComponent(block.location || "") + "&hl=en",
                  id: `${block._id}-map`
                }}
                class="clickable focusable flex flex-grow items-center truncate rounded bg-white p-2 px-3 text-15 font-medium text-gray-700 shadow outline-none focus:outline-none">
                <span class="material-icons notranslate -ml-1 mr-1">location_on</span>
                Open maps
              </LinkContent>
            </div>
            <div class="z-0 h-full opacity-75">
              <GoogleMap address={view_block.location} width={300} height={600} staticMapUrl={view_block.staticMapUrl} />
            </div>
          </div>
        {/if}
      </div>
    </div>
  </div>

  {#if block.collectRSVP}
    <div class="event-rsvp-container border-b border-gray-200 bg-gray-50 p-2">
      {#if !isEditor()}
        <form
          class="new-event-rsvp-form flex w-full flex-col items-start gap-x-2 gap-y-4 sm:flex-row"
          data-event-id={block._id}
          data-page-id={id}>
          <div class="flex w-full items-start gap-2">
            <div class="flex w-full flex-col items-start gap-y-2">
              <label class="text-xs text-primary" for="first_name">First Name</label>
              <input
                name="first_name"
                id="first_name"
                class="text form-field first-name focusable peer mr-1 w-full rounded border p-1 text-sm"
                type="text"
                required
                placeholder="Type here"
                aria-required="true"
                autocomplete="given-name" />
              <p class="hidden text-xs font-normal text-danger-700 peer-[:user-invalid]:block">Please provide first last.</p>
            </div>
            <div class="flex w-full flex-col items-start gap-y-2">
              <label class="text-xs text-primary" for="last_name">Last Name</label>
              <input
                name="last_name"
                id="last_name"
                class="text form-field last-name focusable peer mr-1 w-full rounded border p-1 text-sm"
                type="text"
                required
                placeholder="Type here"
                autocomplete="family-name"
                aria-required="true" />
              <p class="hidden text-xs font-normal text-danger-700 peer-[:user-invalid]:block">Please provide last name.</p>
            </div>
          </div>
          <div class="flex w-full items-start gap-2">
            <div class="flex w-full flex-col items-start gap-y-2">
              <label class="text-xs text-primary" for="email">Email</label>
              <input
                name="email"
                id="email"
                class="text form-field email-address in focusable peer mr-1 w-full flex-grow rounded border p-1 text-sm"
                type="email"
                required
                placeholder="Type here"
                autocomplete="email"
                aria-required="true" />
              <p class="hidden text-xs font-normal text-danger-700 peer-[:user-invalid]:block">Please provide a valid email address.</p>
            </div>
            <div class="flex shrink-0 flex-col items-start gap-y-2">
              <span class="invisible text-xs text-transparent" aria-hidden="true">&nbsp;</span>
              <input
                type="submit"
                class="clickable ada-bg-white ada-text-black focusable block rounded bg-success-600 p-2 text-sm font-semibold text-white"
                value="Attend (RSVP)" />
            </div>
          </div>
        </form>
      {/if}
      <div class="event-author-info flex items-center text-12" class:hidden={!isEditor()}>
        <i class="material-icons notranslate -mt-0.5 mr-1 text-15 text-success-600">check_box</i>
        <p>RSVPs are enabled for this event.</p>
      </div>
    </div>
  {/if}
</Block>

<style>
  :global(.author-show .new-event-rsvp-form) {
    display: none !important;
  }

  :global(.author-show .event-author-info) {
    display: flex !important;
  }
</style>
